import React, { useEffect } from 'react';
import { Button, Group, Text } from '@inexture/core';
import { useSendMessageMutation } from '@services/contact.service.ts';
import {
  getDomain,
  makeAutoSlug,
  MAX_COMPANY_NAME,
  RegisterFormT,
  sanitizeURL
} from '@/form/register/register.utils.ts';
import RegisterHeader from '@/form/register/RegisterHeader.tsx';
import RegisterSuccess from '@/form/register/RegisterSuccess.tsx';
import { FormProvider, useForm } from 'react-hook-form';
import { TextInput } from '@/form/elements/components';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  registerInitialValues,
  registerSchema,
  RegisterSchemaT
} from '@/form/register/register.schema.ts';
import { closeAllModals } from '@inexture/modals';
import { applyServerErrors } from '@/form/server.errors.ts';
import { toast } from 'sonner';

const RegisterForm: React.FC = () => {
  const SUFFIX = `.${getDomain() || 'solvere.be'}`;
  const [sendMessage, { isLoading, isSuccess }] = useSendMessageMutation();

  const methods = useForm<RegisterSchemaT>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(registerSchema),
    defaultValues: registerInitialValues
  });

  // watch company_name so we can auto-generate slug whenever it changes
  const companyName = methods.watch('company_name');

  // ALWAYS auto-generate slug from company_name when company_name changes.
  // Users can still manually edit the slug input; the next change to company_name
  // will overwrite it with the auto slug.
  useEffect(() => {
    const autoSlug = makeAutoSlug(companyName || '');
    const currentSlug = methods.getValues('company_slug') || '';
    if (currentSlug !== autoSlug) {
      methods.setValue('company_slug', autoSlug, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]);

  // enforce max length on company_name — keep input markup unchanged, just truncate before setting
  function handleCompanyNameChange(raw: string) {
    const v = raw.slice(0, MAX_COMPANY_NAME);
    methods.setValue('company_name', v, {
      shouldValidate: true,
      shouldDirty: true
    });
  }

  // sanitize slug on manual edits (user can still type whatever; changes to company_name will override)
  function handleSlugChange(raw: string) {
    const sanitized = sanitizeURL(raw).slice(0, MAX_COMPANY_NAME);
    methods.setValue('company_slug', sanitized, {
      shouldValidate: true,
      shouldDirty: true
    });
  }

  async function handleSubmit(values: RegisterFormT) {
    const payload = {
      ...values,
      company_slug: `${values.company_slug || ''}${SUFFIX}`
    };
    console.log('Payload:', payload);
    try {
      await sendMessage(payload).unwrap();
      setTimeout(() => {
        methods.reset();
        closeAllModals();
      }, 2000);
    } catch (err: any) {
      const parsed = applyServerErrors(methods, err);

      if (parsed.nonFieldErrors.length) {
        parsed.nonFieldErrors.forEach((m: any) => toast.error(m));
      }
    }
  }

  return (
    <div className="relative">
      {isSuccess ? (
        <RegisterSuccess />
      ) : (
        <>
          <RegisterHeader />
          <div className="p-5">
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(handleSubmit)}>
                <div className="grid grid-rows-1 gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <TextInput
                      name="first_name"
                      props={{
                        placeholder: 'Voornaam',
                        withAsterisk: true
                      }}
                    />
                    <TextInput
                      name="last_name"
                      props={{
                        placeholder: 'Achternaam',
                        withAsterisk: true
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-2">
                    <TextInput
                      name="company_name"
                      props={{
                        placeholder: 'Bedrijf',
                        withAsterisk: true,
                        maxLength: MAX_COMPANY_NAME,
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                          handleCompanyNameChange(e.target.value)
                      }}
                    />

                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <TextInput
                          name="company_slug"
                          props={{
                            placeholder: 'URL',
                            withAsterisk: true,
                            maxLength: MAX_COMPANY_NAME,
                            onChange: (
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => handleSlugChange(e.target.value),
                            rightSection: <Text fz="sm">{SUFFIX}</Text>,
                            rightSectionWidth: Math.max(90, SUFFIX.length * 8)
                          }}
                        />
                      </div>
                    </div>

                    <TextInput
                      name="company_registration"
                      props={{
                        placeholder: 'Erkenningsnummer',
                        withAsterisk: true
                      }}
                    />

                    <TextInput
                      name="email"
                      props={{
                        placeholder: 'Email',
                        withAsterisk: true,
                        type: 'email'
                      }}
                    />

                    <TextInput
                      name="contact"
                      props={{
                        placeholder: 'Contactnummer',
                        withAsterisk: true,
                        type: 'tel'
                      }}
                    />
                  </div>
                </div>

                <Group justify="center" my="md">
                  <Button size="md" type="submit" loading={isLoading}>
                    <span className="text-sm">Maak Mijn SOLVERE ACCOUNT</span>
                  </Button>
                </Group>
              </form>
            </FormProvider>
          </div>
        </>
      )}
    </div>
  );
};

export default RegisterForm;
